import snapshot from '@/data/marketsSnapshot.json';
import { getCountryName } from '@/data/countries';

/**
 * The canonical market list (support #238): beatly-core, served by api-v2 at GET /app/v2/reference/markets.
 * `data/marketsSnapshot.json` is a copy of that list bundled with Omni. It is shown while the call loads or when it fails,
 * and fills what an older api-v2 does not send yet (region, language, population, markets it does not list).
 * Live values win over the snapshot; a snapshot market is never dropped. Same rules as BO's supportedMarkets.ts.
 */

/** A market with every field filled (after the merge with the snapshot). */
export interface Market {
  code: string;
  label: string;
  currency: string;
  region: string;
  language: string;
  population: number;
}

export interface Markets {
  countries: Market[];
  regions: string[];
}

/** Codes people use that are not the ISO code: the UK is GB (Meta, api-v2 and the list all use GB). */
export const MARKET_CODE_ALIASES: Readonly<Record<string, string>> = { UK: 'GB' };

/** "uk", " se " → "GB", "SE": upper case, trimmed, aliases resolved. */
export function normalizeMarketCode(code: string | null | undefined): string {
  const up = String(code ?? '').trim().toUpperCase();
  return MARKET_CODE_ALIASES[up] ?? up;
}

/** Group for a market whose region is not known (a live market the snapshot lacks, or a name outside the list). */
export const OTHER_REGION = 'Other';

/** The currencies creators are paid in, one per market (from the list). The FX table has a rate for each. */
export const PAY_CURRENCIES = ['SEK', 'EUR', 'DKK', 'GBP', 'NOK', 'USD', 'PLN', 'CAD'] as const;

const byLabel = (a: { label: string }, b: { label: string }) => a.label.localeCompare(b.label);

/** The bundled list. */
export const MARKETS_FALLBACK: Markets = {
  countries: (snapshot.countries as Market[]).map((c) => ({ ...c })).sort(byLabel),
  regions: [...snapshot.regions],
};

const text = (x: unknown): string | null => (typeof x === 'string' && x.trim() ? x.trim() : null);
const positive = (x: unknown): number | null => (typeof x === 'number' && Number.isFinite(x) && x > 0 ? x : null);

/**
 * The live response laid over the snapshot. Accepts the old shape (code/label/currency) and the new one, and anything
 * malformed (null, missing arrays, odd rows): it never throws and never loses a snapshot market.
 */
export function mergeMarkets(live: unknown, base: Markets = MARKETS_FALLBACK): Markets {
  const l = (live && typeof live === 'object' ? live : {}) as { countries?: unknown; regions?: unknown };
  const byCode = new Map<string, Market>(base.countries.map((c) => [c.code, { ...c }]));
  for (const row of Array.isArray(l.countries) ? l.countries : []) {
    if (!row || typeof row !== 'object') continue;
    const r = row as Record<string, unknown>;
    const code = normalizeMarketCode(text(r.code));
    if (!/^[A-Z]{2}$/.test(code)) continue;
    const known = byCode.get(code);
    byCode.set(code, {
      code,
      label: text(r.label) ?? known?.label ?? getCountryName(code),
      currency: text(r.currency)?.toUpperCase() ?? known?.currency ?? 'EUR',
      region: text(r.region) ?? known?.region ?? OTHER_REGION,
      language: text(r.language)?.toLowerCase() ?? known?.language ?? 'en',
      population: positive(r.population) ?? known?.population ?? 0,
    });
  }
  const countries = [...byCode.values()].sort(byLabel);
  const liveRegions = Array.isArray(l.regions) ? l.regions.map(text).filter((x): x is string => !!x) : [];
  const regions = [...new Set([...(liveRegions.length ? liveRegions : base.regions), ...base.regions])];
  for (const c of countries) if (!regions.includes(c.region)) regions.push(c.region);
  return { countries, regions };
}

/** Lower case, accents dropped: "Türkiye" and "turkiye" match. */
export const foldText = (s: string) => s.normalize('NFD').replace(/\p{M}+/gu, '').toLowerCase().trim();

/** Names people (and older saved data) use for a market that are not its list name. */
const NAME_ALIASES: Readonly<Record<string, string>> = {
  uk: 'GB', 'great britain': 'GB', england: 'GB', britain: 'GB',
  usa: 'US', 'united states of america': 'US', america: 'US',
  czechia: 'CZ', turkiye: 'TR', holland: 'NL', 'the netherlands': 'NL',
  sverige: 'SE', norge: 'NO', danmark: 'DK', suomi: 'FI', deutschland: 'DE', brasil: 'BR',
};

/** The market for a code ("SE", "uk") or a name ("Sweden", "Türkiye", "the Netherlands"); undefined when unknown. */
export function findMarket(codeOrName: string | null | undefined, countries: readonly Market[] = MARKETS_FALLBACK.countries): Market | undefined {
  const raw = String(codeOrName ?? '').trim();
  if (!raw) return undefined;
  const code = normalizeMarketCode(raw);
  const byCode = /^[A-Z]{2}$/.test(code) ? countries.find((m) => m.code === code) : undefined;
  if (byCode) return byCode;
  const q = foldText(raw);
  const alias = NAME_ALIASES[q];
  return countries.find((m) => foldText(m.label) === q) ?? (alias ? countries.find((m) => m.code === alias) : undefined);
}

/** "Sweden" for SE; any other code reads as its ISO name, else the code. */
export function marketLabel(code: string, countries: readonly Market[] = MARKETS_FALLBACK.countries): string {
  const c = normalizeMarketCode(code);
  return countries.find((m) => m.code === c)?.label ?? getCountryName(c);
}

/** The market's main language (ISO 639-1), by code or name; English when the list has none for it. */
export function marketLanguage(codeOrName: string, countries: readonly Market[] = MARKETS_FALLBACK.countries): string {
  return findMarket(codeOrName, countries)?.language || 'en';
}

/** Type-to-filter: the query is part of the name, or the start of the code ("se", "Swe", "united"). */
export function marketMatches(market: Pick<Market, 'code' | 'label'>, query: string): boolean {
  const q = foldText(query);
  if (!q) return true;
  return foldText(market.label).includes(q) || market.code.toLowerCase().startsWith(q);
}

/** The markets per region, regions in the list's order; empty regions are left out. */
export function groupMarketsByRegion<T extends Pick<Market, 'region'>>(countries: readonly T[], regions: readonly string[]): Array<{ region: string; markets: T[] }> {
  const order = [...regions];
  for (const c of countries) if (!order.includes(c.region)) order.push(c.region);
  return order.map((region) => ({ region, markets: countries.filter((c) => c.region === region) })).filter((g) => g.markets.length);
}

/** Code → name for every market in the list (labels for stored codes, e.g. the kickoff countries field). */
export const MARKET_NAMES: Readonly<Record<string, string>> = Object.fromEntries(MARKETS_FALLBACK.countries.map((m) => [m.code, m.label]));

/**
 * A stored market value as its parts: "Sweden + Norway", "SE, NO", "Sweden/Denmark", "Sweden & Norway" → two parts.
 * Splits only on + , / & (and on the word "and" only when both sides are markets, so "Bosnia and Herzegovina" and
 * "Trinidad and Tobago" stay whole). Every part is kept as written, known or not ("UK", "Nordics", "JP").
 */
export function splitMarkets(value: string | null | undefined, countries: readonly Market[] = MARKETS_FALLBACK.countries): string[] {
  const out: string[] = [];
  for (const piece of String(value ?? '').split(/[+,/&]/)) {
    const p = piece.trim();
    if (!p) continue;
    const words = p.split(/\s+and\s+/i);
    if (words.length > 1 && !findMarket(p, countries) && words.every((w) => findMarket(w, countries))) out.push(...words.map((w) => w.trim()));
    else out.push(p);
  }
  return out;
}

/** Parts back into one stored value. */
export const joinMarkets = (parts: readonly string[]) => parts.join(', ');
